/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SignUpButton } from '@clerk/clerk-react';
import { Button } from './components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Switch } from './components/ui/switch';
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from './components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Menu, Info, Guitar, Keyboard as KeyboardIcon, Headphones, Volume2, Target, Brain, TrendingUp, Newspaper, Users, Lightbulb, Play, Pause, Mail, GraduationCap } from 'lucide-react';

import {
  keyOptions,
  chordOptions,
  scaleOptions,
  findKeyOption,
  getPreferredKeyValue,
  AccidentalPreference,
  isScaleAvailableForFree,
  isChordAvailableForFree,
  isKeyAvailableForFree
} from './musicOptions';
import Fretboard, { FretPosition } from './components/Fretboard';
import GlobalChat from './components/chat/GlobalChat';
import ChordFingeringSelector from './components/ChordFingeringSelector';
import Keyboard from './components/Keyboard';
import InstrumentConfig from './components/InstrumentConfig';
import IntervalsLegend from './components/IntervalsLegend';
import DiatonicChords from './components/DiatonicChords';
import FretRangeSelector from './components/FretRangeSelector';
import EarTrainingLearn from './components/EarTraining/EarTrainingLearn';
import EarTrainingPractice from './components/EarTraining/EarTrainingPractice';
import EarTrainingTest from './components/EarTraining/EarTrainingTest';
import EarTrainingProgress from './components/EarTraining/EarTrainingProgress';
import FretboardLearn from './components/FretboardQuiz/FretboardLearn';
import FretboardPractice from './components/FretboardQuiz/FretboardPractice';
import FretboardTest from './components/FretboardQuiz/FretboardTest';
import FretboardProgress from './components/FretboardQuiz/FretboardProgress';
import BackingTrackPanel from './components/JamTrack/BackingTrackPanel';
import JamTrack from './components/JamTrack/JamTrack';
import TimelineAdmin from './components/Admin/TimelineAdmin';
import TabAnimationControls from './components/TabAnimation/TabAnimationControls';
import { useTabAnimation } from './hooks/useTabAnimation';
import { parseTab } from './utils/tabParser';
import { DEFAULT_TAB_PARSER_CONFIG } from './types/tabAnimation';
import OnboardingGuide from './components/OnboardingGuide/OnboardingGuide';
import TutorialPrompt from './components/OnboardingGuide/TutorialPrompt';
import useOnboarding from './hooks/useOnboarding';
import useTheoryPrimer from './hooks/useTheoryPrimer';
import { useChat } from './context/ChatContext';
import { useActivityLoggerContext } from './context/ActivityLoggerContext';
import { useSkillLevel } from './context/SkillLevelContext';
import type { SkillLevel } from './types/skillLevel';
import { SKILL_LEVEL_FRET_DEFAULTS, SKILL_LEVEL_STORAGE_KEY } from './types/skillLevel';
import './components/OnboardingGuide/onboarding.css';
import { NoteCollection, ChordFingering, fetchChordNotes, fetchScaleNotes, fetchChordOfScaleDegree, fetchChordFingerings, ChatCapabilityLinkDto } from './api';
import type { SelectedDiatonicChord } from './components/DiatonicChords';
import type { BackingTrack } from './types/jamTrack';
import DailyNewsPanel from './components/DailyNewsPanel';
import { AuthButtons, SubscriptionBadge } from './components/auth';
import { useAuth } from './hooks/useAuth';
import { useScaleAnimation } from './hooks/useScaleAnimation';
import { useAgentConnection, ShowChordPayload, ShowScalePayload, AnimateTabPayload } from './hooks/useAgentConnection';
import { CreatorDirectoryDialog } from './components/creators/CreatorDirectoryDialog';
import { useCreatorProfiles } from './components/creators/useCreatorProfiles';
import TheoryPrimer from './components/TheoryPrimer';
import TheoryCard from './components/TheoryCard';
import MobileChordFingerings from './components/MobileChordFingerings';
import MobileContentAccordion from './components/MobileContentAccordion';
import MobileControlBar from './components/MobileControlBar';
import MobileToolsSheet from './components/MobileToolsSheet';
import MobilePracticePanel from './components/MobilePracticePanel';
import MobileLearningPanel from './components/MobileLearningPanel';
import MobileTutorialPanel from './components/MobileTutorialPanel';
import DemoBanner from './components/DemoBanner';
import AnonymousBanner from './components/AnonymousBanner';
import { clearDemoSession } from './utils/demoMode';
import type { DemoUser } from './utils/demoMode';
import { UpgradeButton, SubscriptionSuccess, SubscriptionCancel, PricingModal } from './components/subscription';


// Parse ?key=D&chord=Minor7 or ?key=G&scale=pentatonicminor on initial load.
// Values are validated against the options lists — unrecognised values are ignored.
// The existing Free-tier enforcement effect clamps anything above the user's tier.
function parseUrlParams(): { key?: string; chordOrScale?: string; isChord?: boolean } {
  const params = new URLSearchParams(window.location.search);
  const result: { key?: string; chordOrScale?: string; isChord?: boolean } = {};

  const keyParam = params.get('key');
  if (keyParam) {
    const match = findKeyOption(keyParam);
    if (match) result.key = match.sharp;
  }

  const chordParam = params.get('chord');
  const scaleParam = params.get('scale');

  if (chordParam) {
    const match = chordOptions.find(o => o.value.toLowerCase() === chordParam.toLowerCase());
    if (match) { result.chordOrScale = match.value; result.isChord = true; }
  } else if (scaleParam) {
    const match = scaleOptions.find(o => o.value.toLowerCase() === scaleParam.toLowerCase());
    if (match) { result.chordOrScale = match.value; result.isChord = false; }
  }

  return result;
}

// Interface for diatonic chord data
interface DiatonicChord {
  key: string;
  scale: string;
  degree: number;
  chordRoot: string;
  chordType: string;
  romanNumeral: string;
  chordNotes: string[];
}

// Helper function to get display name
const getDisplayName = (value: string, isChord: boolean): string => {
  if (isChord) {
    const chordOption = chordOptions.find(option => option.value === value);
    return chordOption ? chordOption.display : value;
  } else {
    const scaleOption = scaleOptions.find(option => option.value === value);
    return scaleOption ? scaleOption.display : value;
  }
};

const summarizeNoteCollection = (collection?: NoteCollection | null) => {
  if (!collection?.notesAndIntervals?.length) {
    return undefined;
  }

  return collection.notesAndIntervals
    .map(({ note, interval }) => {
      const intervalLabel = interval?.abbreviation || interval?.intervalName;
      return intervalLabel ? `${note.note} (${intervalLabel})` : note.note;
    })
    .join(', ');
};

const App: React.FC = () => {
  const { registerContext, unregisterContext, messages, sessionId } = useChat();
  const primaryContextKeyRef = useRef<string | null>(null);
  const diatonicContextKeyRef = useRef<string | null>(null);

  // Auth and subscription
  const { userProfile, isFree, canSelectKey, subscriptionTier, isLoading: isAuthLoading, isAnonymous, demoUser } = useAuth();

  // Activity logging
  const { logKeyChange, logScaleChange, logChordChange, logModeToggle, setMusicalContext, setInstrumentContext } = useActivityLoggerContext();

  // Skill level - controls UI complexity
  const { skillLevel, setSkillLevel, features: skillFeatures } = useSkillLevel();

  // Demo mode state
  const [showDemoBanner, setShowDemoBanner] = useState(true);
  const [showAnonymousBanner, setShowAnonymousBanner] = useState(true);

  // Subscription page routing (simple URL-based)
  const [subscriptionPage, setSubscriptionPage] = useState<'success' | 'cancel' | null>(() => {
    const path = window.location.pathname;
    if (path === '/subscription/success') return 'success';
    if (path === '/subscription/cancel') return 'cancel';
    return null;
  });
  const [showPricingModal, setShowPricingModal] = useState(false);

  // Core state management - beginners start with C Major chord, others with C Ionian scale
  const initialSkillLevel = localStorage.getItem(SKILL_LEVEL_STORAGE_KEY) as SkillLevel | null;
  const isBeginnerDefault = !initialSkillLevel || initialSkillLevel === 'beginner';
  const urlParams = parseUrlParams();
  const [key, setKey] = useState(urlParams.key ?? 'C');
  const [chordOrScale, setChordOrScale] = useState(urlParams.chordOrScale ?? (isBeginnerDefault ? 'major' : 'ionian'));
  const [isChord, setIsChord] = useState(urlParams.isChord ?? isBeginnerDefault);
  const [accidentalPreference, setAccidentalPreference] = useState<AccidentalPreference>('sharps');
  const [appMode, setAppMode] = useState<'scale' | 'chord' | 'earTraining' | 'compare'>(
    urlParams.isChord !== undefined ? (urlParams.isChord ? 'chord' : 'scale') : (isBeginnerDefault ? 'chord' : 'scale')
  );

  // Compare mode state - dual fretboards
  const [key1, setKey1] = useState('C');
  const [chordOrScale1, setChordOrScale1] = useState('ionian');
  const [isChord1, setIsChord1] = useState(false);
  const [noteCollection1, setNoteCollection1] = useState<NoteCollection | null>(null);

  const [key2, setKey2] = useState('G');
  const [chordOrScale2, setChordOrScale2] = useState('ionian');
  const [isChord2, setIsChord2] = useState(false);
  const [noteCollection2, setNoteCollection2] = useState<NoteCollection | null>(null);
  const { creators, isLoading: isCreatorsLoading } = useCreatorProfiles();
  const [isCreatorDialogOpen, setCreatorDialogOpen] = useState(false);

  const applyAccidentalPreference = useCallback(
    (currentKey: string) => getPreferredKeyValue(currentKey, accidentalPreference),
    [accidentalPreference]
  );

  useEffect(() => {
    setKey(prev => applyAccidentalPreference(prev));
    setKey1(prev => applyAccidentalPreference(prev));
    setKey2(prev => applyAccidentalPreference(prev));
  }, [accidentalPreference, applyAccidentalPreference]);

  // Keep activity logger musical context in sync
  useEffect(() => {
    setMusicalContext({
      key,
      scaleOrChord: chordOrScale,
      mode: isChord ? 'chord' : 'scale',
      accidentalPreference,
    });
  }, [key, chordOrScale, isChord, accidentalPreference, setMusicalContext]);

  // Update fret range when skill level changes
  const prevSkillLevelRef = useRef(skillLevel);
  useEffect(() => {
    if (prevSkillLevelRef.current !== skillLevel) {
      const defaults = SKILL_LEVEL_FRET_DEFAULTS[skillLevel];
      setFretRange({ start: defaults.min, end: defaults.max });
      prevSkillLevelRef.current = skillLevel;
    }
  }, [skillLevel]);

  // Auto-select open C chord fingering for beginners on first load
  const hasAutoSelectedFingering = useRef(false);
  useEffect(() => {
    const autoSelectBeginnerFingering = async () => {
      // Only run once, for beginners, in chord mode, with C Major
      if (
        hasAutoSelectedFingering.current ||
        skillLevel !== 'beginner' ||
        appMode !== 'chord' ||
        key !== 'C' ||
        chordOrScale !== 'major'
      ) {
        return;
      }

      try {
        const fingerings = await fetchChordFingerings('C', 'major');
        if (fingerings && fingerings.length > 0) {
          // Find the open C chord (C form, root on string 5) or use first fingering
          const openCFingering = fingerings.find(
            (f: any) => f.Name?.toLowerCase().includes('c form') ||
                       f.name?.toLowerCase().includes('c form') ||
                       (f.StartingFret === 0 || f.startingFret === 0)
          ) || fingerings[0];

          // Convert to camelCase if needed
          const fingering: ChordFingering = {
            name: openCFingering.Name || openCFingering.name,
            positionString: openCFingering.PositionString || openCFingering.positionString,
            fingers: openCFingering.FingerString || openCFingering.fingers,
            startingFret: openCFingering.StartingFret ?? openCFingering.startingFret ?? 0,
            barreFret: openCFingering.BarreFret ?? openCFingering.barreFret,
            positions: (openCFingering.Positions || openCFingering.positions || []).map((p: any) => ({
              stringIndex: p.StringIndex ?? p.stringIndex,
              fret: p.Fret ?? p.fret,
              finger: p.Finger ?? p.finger,
              isMuted: p.IsMuted ?? p.isMuted ?? false,
              isOpen: p.IsOpen ?? p.isOpen ?? false,
              note: p.Note ?? p.note
            }))
          };

          setSelectedFingering(fingering);
          setChordHighlightType('fingering');
          hasAutoSelectedFingering.current = true;
        }
      } catch (error) {
        console.error('Error auto-selecting fingering for beginner:', error);
      }
    };

    autoSelectBeginnerFingering();
  }, [skillLevel, appMode, key, chordOrScale]);

  // Demo mode is now initialized in useAuth hook

  // Enforce Free tier restrictions
  useEffect(() => {
    if (isFree) {
      // Deep links (URL params) are allowed to display any content — the locked
      // dropdowns prevent the user from navigating to other premium content.
      const search = new URLSearchParams(window.location.search);
      if (search.has('key') || search.has('chord') || search.has('scale')) return;

      // Lock key to C for Free tier
      if (!isKeyAvailableForFree(key)) {
        setKey('C');
      }

      // Reset to valid chord/scale if current selection is not allowed
      // Note: Preview modes (dorian, mixolydian) will show grayed but shouldn't be auto-selected
      const isAllowed = isChord
        ? isChordAvailableForFree(chordOrScale)
        : isScaleAvailableForFree(chordOrScale);

      if (!isAllowed) {
        setChordOrScale(isChord ? 'major' : 'ionian');
      }
    }
  }, [isFree, key, chordOrScale, isChord]);

  const renderKeyOptions = () =>
    keyOptions.map(option => {
      const valueForPreference =
        accidentalPreference === 'flats' && option.flat ? option.flat : option.sharp;
      const isLocked = (isAnonymous || isFree) && !isKeyAvailableForFree(valueForPreference);

      return (
        <SelectItem
          key={`${option.sharp}-${valueForPreference}`}
          value={valueForPreference}
          disabled={isLocked}
          className={isLocked ? 'opacity-60' : ''}
        >
          <span className="flex items-center gap-2">
            {valueForPreference}
            {isLocked && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-indigo-500/10 text-indigo-600 border-indigo-500/30">
                Pro
              </Badge>
            )}
          </span>
        </SelectItem>
      );
    });

  const renderAccidentalPreferenceControl = () => {
    // Hide for beginners
    if (!skillFeatures.showAccidentalToggle) {
      return null;
    }
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium">Accidentals</label>
        <div className="grid grid-cols-2 overflow-hidden rounded-md border border-input bg-card/60">
          <Button
            variant={accidentalPreference === 'sharps' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-none border-0 px-3 py-2 focus-visible:ring-0"
            onClick={() => setAccidentalPreference('sharps')}
          >
            Sharps
          </Button>
          <Button
            variant={accidentalPreference === 'flats' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-none border-0 px-3 py-2 focus-visible:ring-0"
            onClick={() => setAccidentalPreference('flats')}
          >
            Flats
          </Button>
        </div>
      </div>
    );
  };
  
  // Onboarding guide
  const { shouldShowOnboarding, shouldShowTutorialPrompt, completeOnboarding, skipOnboarding, showOnboarding, dismissTutorialPrompt } = useOnboarding();
  const { shouldShowTheoryPrimer, shouldShowTheoryCard, dismissTheoryPrimer, dismissTheoryCard, showTheoryHints } = useTheoryPrimer();

  const [showNoteNames, setShowNoteNames] = useState(true);
  const [isVertical, setIsVertical] = useState(true);
  const [strings, setStrings] = useState(6);
  const [frets, setFrets] = useState(22);
  const [tuning, setTuning] = useState(['E', 'A', 'D', 'G', 'B', 'E']);
  const [fretRange, setFretRange] = useState(() => {
    // Initialize fret range based on stored skill level
    const storedLevel = localStorage.getItem(SKILL_LEVEL_STORAGE_KEY) as SkillLevel | null;
    const level = storedLevel || 'beginner';
    const defaults = SKILL_LEVEL_FRET_DEFAULTS[level];
    return { start: defaults.min, end: defaults.max };
  });
  const [rightPanelView, setRightPanelView] = useState<'keyboard' | 'chordFingeringSelector' | 'diatonicChords' | 'earTrainingLearn' | 'earTrainingPractice' | 'earTrainingTest' | 'earTrainingProgress' | 'jamTracks' | 'scaleInfo'>(
    urlParams.isChord === true ? 'chordFingeringSelector'
      : isBeginnerDefault ? 'chordFingeringSelector'
      : 'diatonicChords'
  );
  
  // Practice sub-mode state
  const [practiceSubMode, setPracticeSubMode] = useState<'earTraining' | 'fretboardQuiz'>('earTraining');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isNewsDialogOpen, setIsNewsDialogOpen] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileToolsSheetOpen, setMobileToolsSheetOpen] = useState(false);
  const [noteCollection, setNoteCollection] = useState<NoteCollection | null>(null);
  const [selectedFingering, setSelectedFingering] = useState<ChordFingering | null>(null);
  const [selectedDiatonicChord, setSelectedDiatonicChord] = useState<SelectedDiatonicChord | null>(null);
  const [chordHighlightType, setChordHighlightType] = useState<'fingering' | 'diatonic' | null>(null);
  
  // New state for diatonic chords
  const [diatonicChords, setDiatonicChords] = useState<DiatonicChord[]>([]);
  
  // Ear training state
  const [earTrainingSession, setEarTrainingSession] = useState({
    isActive: false,
    currentExercise: null,
    score: { correct: 0, total: 0, streak: 0 },
    settings: { difficulty: 'beginner', exerciseType: 'intervals' }
  });
  
  // Interactive fretboard state for ear training
  const [fretboardInteractive, setFretboardInteractive] = useState(false);
  const [rootNotePosition, setRootNotePosition] = useState<FretPosition | null>(null);
  const [userFretboardAnswers, setUserFretboardAnswers] = useState<FretPosition[]>([]);
  
  const [correctUserFretboardAnswers, setCorrectUserFretboardAnswers] = useState<FretPosition[]>([]);
  const [incorrectUserFretboardAnswers, setIncorrectUserFretboardAnswers] = useState<FretPosition[]>([]);
  const [highlightedFretPositions, setHighlightedFretPositions] = useState<FretPosition[]>([]);

  // Jam track state
  const [isJamTrackMode, setIsJamTrackMode] = useState(false);
  const [selectedBackingTrack, setSelectedBackingTrack] = useState<BackingTrack | null>(null);
  const [availableTracksCount, setAvailableTracksCount] = useState(0);
  const [preJamTrackFretRange, setPreJamTrackFretRange] = useState<{ start: number; end: number } | null>(null);
  const [autoJamTrackRangeApplied, setAutoJamTrackRangeApplied] = useState(false);

  // Scale pattern animation
  const { isAnimating: isScaleAnimating, currentAnimatedNote, toggleAnimation: toggleScaleAnimation } = useScaleAnimation({
    noteCollection: appMode === 'compare' ? null : noteCollection,
    strings,
    fretRange,
    tuning,
    isEnabled: !isChord && appMode !== 'compare'
  });

  // Tab animation - integrated hook
  const {
    state: tabAnimationState,
    currentPositions: tabCurrentPositions,
    dispatch: tabAnimationDispatch,
    loadSequence: loadTabSequence,
    progress: tabProgress,
    sequence: tabSequence
  } = useTabAnimation();

  // SignalR connection for agentic chat fretboard control
  // Handles real-time updates when the AI assistant shows chords/scales
  const handleAgentShowChord = useCallback((payload: ShowChordPayload) => {
    console.log('[Agent] ShowChord received:', payload);
    setKey(payload.root);
    setChordOrScale(payload.chordType);
    setIsChord(true);
    setAppMode('chord');
    // Clear any existing fingering selection - auto-select will pick the first one
    setSelectedFingering(null);
    setChordHighlightType(null);
    // Switch to fingerings tab so user sees the chord fingerings
    if (isStandardTuning(tuning, strings)) {
      setRightPanelView('chordFingeringSelector');
    }
  }, [tuning, strings]);

  const handleAgentShowScale = useCallback((payload: ShowScalePayload) => {
    console.log('[Agent] ShowScale received:', payload);
    setKey(payload.root);
    setChordOrScale(payload.scaleType);
    setIsChord(false);
    setAppMode('scale');
    // Clear any existing selections
    setSelectedFingering(null);
    setSelectedDiatonicChord(null);
    setChordHighlightType(null);
    // Switch to diatonic chords tab for scale mode
    setRightPanelView('diatonicChords');
  }, []);

  const handleAgentClearFretboard = useCallback(() => {
    console.log('[Agent] ClearFretboard received');
    setSelectedFingering(null);
    setSelectedDiatonicChord(null);
    setChordHighlightType(null);
  }, []);

  const handleAgentAnimateTab = useCallback((payload: AnimateTabPayload) => {
    console.log('[Agent] AnimateTab received:', payload.tabText.substring(0, 100));
    const parseResult = parseTab(payload.tabText, {
      ...DEFAULT_TAB_PARSER_CONFIG,
      dashesPerBeat: 4
    });

    if (parseResult.sequence) {
      console.log('[Agent] Tab parsed successfully:', parseResult.sequence);
      loadTabSequence(parseResult.sequence);
      setTimeout(() => tabAnimationDispatch({ type: 'PLAY' }), 100);
    } else {
      console.error('[Agent] Tab parsing errors:', parseResult.errors);
    }
  }, [loadTabSequence, tabAnimationDispatch]);

  const { isConnected: isAgentConnected } = useAgentConnection(sessionId, {
    onShowChord: handleAgentShowChord,
    onShowScale: handleAgentShowScale,
    onClearFretboard: handleAgentClearFretboard,
    onAnimateTab: handleAgentAnimateTab,
  });

  // Log agent connection status changes
  useEffect(() => {
    if (sessionId) {
      console.log('[Agent] SignalR connection status:', isAgentConnected ? 'Connected' : 'Disconnected', 'Session:', sessionId);
    }
  }, [isAgentConnected, sessionId]);

  // Convert tab positions to fret highlights with note calculation
  const calculateNoteFromPosition = (stringNum: number, fret: number): string => {
    if (stringNum < 0 || stringNum >= tuning.length) return 'C';

    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const openStringNote = tuning[stringNum];
    const openStringIndex = notes.findIndex(n => n === openStringNote || n === openStringNote + '#' || n + '#' === openStringNote);

    if (openStringIndex === -1) return 'C';

    const noteIndex = (openStringIndex + fret) % 12;
    return notes[noteIndex];
  };

  const tabAnimationHighlights: FretPosition[] = tabCurrentPositions
    .filter(pos => pos.fret >= 0) // Filter out muted notes
    .map(pos => {
      // Tab parser uses 0=high E, 5=low E (tab convention)
      // Fretboard might use 1-indexed strings
      // Try: fretboardString = strings - tabString (1-indexed inversion)
      const fretboardString = strings - pos.string;

      return {
        string: fretboardString,
        fret: pos.fret,
        note: calculateNoteFromPosition(fretboardString - 1, pos.fret) // Use 0-indexed for note calc
      };
    });

  // Debug logging
  if (tabAnimationHighlights.length > 0 && tabSequence) {
    console.log('Tab animation active:', {
      currentBeat: tabAnimationState.currentBeat,
      rawTabPositions: tabCurrentPositions,
      convertedHighlights: tabAnimationHighlights,
      tuning: tuning,
      strings: strings,
      isPlaying: tabAnimationState.isPlaying
    });
  }

  // Jam track handlers
  const handleTrackSelect = (track: BackingTrack) => {
    const isFirstOctave = fretRange.start === 0 && fretRange.end === 12;
    const isFullNeck = fretRange.start === 0 && fretRange.end >= frets;
    const shouldAutoAdjust = isFirstOctave || isFullNeck;

    if (shouldAutoAdjust && !autoJamTrackRangeApplied) {
      const defaultEnd = Math.min(12, frets);
      const defaultStart = Math.min(5, Math.max(0, defaultEnd - 5));
      const safeEnd = Math.min(frets, Math.max(defaultEnd, defaultStart + 1));
      const adjustedRange = {
        start: defaultStart,
        end: safeEnd
      };

      setPreJamTrackFretRange(fretRange);
      setAutoJamTrackRangeApplied(true);
      setFretRange(adjustedRange);
    } else if (!autoJamTrackRangeApplied) {
      setPreJamTrackFretRange(null);
      setAutoJamTrackRangeApplied(false);
    }

    setSelectedBackingTrack(track);
    setIsJamTrackMode(true);
  };

  // Handle tracks count changes
  const handleTracksCountChange = (count: number) => {
    setAvailableTracksCount(count);
  };

  const handleExitJamTrack = () => {
    setIsJamTrackMode(false);
    setSelectedBackingTrack(null);
    if (autoJamTrackRangeApplied && preJamTrackFretRange) {
      setFretRange(preJamTrackFretRange);
    }
    setPreJamTrackFretRange(null);
    setAutoJamTrackRangeApplied(false);
  };

  // Fetch diatonic chords function
  const fetchDiatonicChords = async (key: string, scaleName: string) => {
    const degrees = [1, 2, 3, 4, 5, 6, 7];
    try {
      const chordPromises = degrees.map(degree => fetchChordOfScaleDegree(key, scaleName, degree));
      const chords = await Promise.all(chordPromises);
      setDiatonicChords(chords);
    } catch (error) {
      console.error('Error fetching diatonic chords:', error);
      setDiatonicChords([]);
    }
  };

  // Function to fetch and update note collection
  const refetchNotes = useCallback(async () => {
    try {
      console.log('Fetching notes for mode:', isChord ? 'chord' : 'scale', 'key:', key, 'selection:', chordOrScale);
      const fetchedNotes = isChord
        ? await fetchChordNotes(key, chordOrScale)
        : await fetchScaleNotes(key, chordOrScale);
      setNoteCollection(fetchedNotes);
      console.log('Notes updated successfully');
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  }, [isChord, key, chordOrScale]);

  useEffect(() => {
    if (appMode === 'compare') {
      if (primaryContextKeyRef.current) {
        unregisterContext(primaryContextKeyRef.current);
        primaryContextKeyRef.current = null;
      }
      return;
    }

    const displayName = getDisplayName(chordOrScale, isChord);
    const scope = isChord ? 'chord' : 'scale';
    const summary = summarizeNoteCollection(noteCollection);

    const newKey = registerContext({
      scope,
      entityId: `${scope}:${key}:${chordOrScale}`,
      displayName: `${key} ${displayName} ${scope}`,
      summary: summary ? `${displayName} notes: ${summary}` : undefined,
      metadata: {
        key,
        selectionType: scope,
        modeOrQuality: displayName,
      },
    });

    if (primaryContextKeyRef.current && primaryContextKeyRef.current !== newKey) {
      unregisterContext(primaryContextKeyRef.current);
    }
    primaryContextKeyRef.current = newKey;

    return () => {
      if (primaryContextKeyRef.current === newKey) {
        unregisterContext(primaryContextKeyRef.current);
        primaryContextKeyRef.current = null;
      }
    };
  }, [
    appMode,
    chordOrScale,
    isChord,
    key,
    noteCollection,
    registerContext,
    unregisterContext,
  ]);

  useEffect(() => {
    if (!selectedDiatonicChord || !selectedDiatonicChord.chord) {
      if (diatonicContextKeyRef.current) {
        unregisterContext(diatonicContextKeyRef.current);
        diatonicContextKeyRef.current = null;
      }
      return;
    }

    const { chord } = selectedDiatonicChord;
    const description = `${chord.romanNumeral} - ${chord.chordRoot}${chord.chordType}`;
    const summary = `Chord tones: ${chord.chordNotes.join(', ')} (Key ${chord.key} ${chord.scale}).`;

    const newKey = registerContext({
      scope: 'diatonic-chord',
      entityId: `diatonic:${chord.key}:${chord.scale}:${chord.romanNumeral}`,
      displayName: description,
      summary,
      metadata: {
        chordRoot: chord.chordRoot,
        chordType: chord.chordType,
        romanNumeral: chord.romanNumeral,
        parentKey: chord.key,
        parentScale: chord.scale,
      },
    });

    if (diatonicContextKeyRef.current && diatonicContextKeyRef.current !== newKey) {
      unregisterContext(diatonicContextKeyRef.current);
    }
    diatonicContextKeyRef.current = newKey;

    return () => {
      if (diatonicContextKeyRef.current === newKey) {
        unregisterContext(diatonicContextKeyRef.current);
        diatonicContextKeyRef.current = null;
      }
    };
  }, [registerContext, selectedDiatonicChord, unregisterContext]);

  // Fetch functions for compare mode fretboards
  const refetchNotes1 = useCallback(async () => {
    try {
      const fetchedNotes = isChord1
        ? await fetchChordNotes(key1, chordOrScale1)
        : await fetchScaleNotes(key1, chordOrScale1);
      setNoteCollection1(fetchedNotes);
    } catch (error) {
      console.error('Error fetching notes for fretboard 1:', error);
    }
  }, [isChord1, key1, chordOrScale1]);

  const refetchNotes2 = useCallback(async () => {
    try {
      const fetchedNotes = isChord2
        ? await fetchChordNotes(key2, chordOrScale2)
        : await fetchScaleNotes(key2, chordOrScale2);
      setNoteCollection2(fetchedNotes);
    } catch (error) {
      console.error('Error fetching notes for fretboard 2:', error);
    }
  }, [isChord2, key2, chordOrScale2]);

  useEffect(() => {
    refetchNotes();

    // Clear fingering selection when key or chord changes
    // This prevents stale fingering highlights when switching chords/keys
    setSelectedFingering(null);
    setChordHighlightType(null);
    setSelectedDiatonicChord(null);

    // Fetch diatonic chords only in scale mode
    if (!isChord) {
      fetchDiatonicChords(key, chordOrScale);
    }
  }, [key, chordOrScale, isChord]);

  // Fetch notes for compare mode fretboards
  useEffect(() => {
    if (appMode === 'compare') {
      refetchNotes1();
    }
  }, [key1, chordOrScale1, isChord1, appMode, refetchNotes1]);

  useEffect(() => {
    if (appMode === 'compare') {
      refetchNotes2();
    }
  }, [key2, chordOrScale2, isChord2, appMode, refetchNotes2]);

  // Handle responsive behavior
  useEffect(() => {
    const mobile = window.innerWidth < 768; // md breakpoint
    setIsMobile(mobile);

    // Set default collapse states based on screen size - ONLY on initial mount
    if (mobile) {
      // On mobile, collapse both panels by default
      setSidebarCollapsed(true);
      setRightPanelCollapsed(true);

      // Auto-restrict fret range for mobile if showing too many frets
      // Optimal mobile experience is ~12 frets; cap at 15 for reasonable UX
      setFretRange(current => {
        const currentSpan = current.end - current.start;
        const maxMobileFrets = 15;
        if (currentSpan > maxMobileFrets) {
          // Preserve the starting position but cap the end
          return { start: current.start, end: Math.min(current.start + maxMobileFrets, frets) };
        }
        return current;
      });
    } else {
      // On desktop/tablet, expand both panels by default
      setSidebarCollapsed(false);
      setRightPanelCollapsed(false);
    }

    // Add resize listener that ONLY updates isMobile state, not panel collapse states
    const handleResize = () => {
      const newMobile = window.innerWidth < 768;
      setIsMobile(newMobile);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [frets]);

  // Handle mode changes
  const handleModeChange = (newMode: 'scale' | 'chord' | 'earTraining' | 'compare') => {
    console.log('Mode change:', appMode, '->', newMode);

    // Log mode toggle (only for scale/chord toggles)
    if ((appMode === 'scale' || appMode === 'chord') && (newMode === 'scale' || newMode === 'chord')) {
      logModeToggle(appMode, newMode);
    }

    setAppMode(newMode);

    // Update legacy isChord state for backward compatibility
    const newIsChord = newMode === 'chord';
    setIsChord(newIsChord);
    console.log('isChord updated:', isChord, '->', newIsChord);

    // Clear selections when switching modes
    setSelectedFingering(null);
    setSelectedDiatonicChord(null);
    setChordHighlightType(null);

      // Select the first item in the dropdown when switching modes
    if (newMode === 'chord') {
      setChordOrScale(chordOptions[0]?.value || '');
    } else if (newMode === 'scale') {
      setChordOrScale(scaleOptions[0]?.value || '');
    }

    // Auto-expand panels and initialize fretboards in Dual View mode
    if (newMode === 'compare') {
      setSidebarCollapsed(false);
      setRightPanelCollapsed(false);

      // Initialize Fretboard A with current selection
      setKey1(key);
      setChordOrScale1(chordOrScale);
      setIsChord1(isChord);

      // Initialize Fretboard B based on current mode
      if (isChord) {
        // If coming from chord mode, mirror the same chord
        setKey2(key);
        setChordOrScale2(chordOrScale);
        setIsChord2(true);
      } else {
        // If coming from scale mode, show the first chord of that scale (I chord)
        setKey2(key);
        setIsChord2(true);

        // Try to get the first diatonic chord if available
        if (diatonicChords.length > 0 && diatonicChords[0]) {
          setChordOrScale2(diatonicChords[0].chordType.toLowerCase());
        } else {
          // Fallback: guess based on scale name (use lowercase to match chordOptions)
          const scaleLower = chordOrScale.toLowerCase();
          if (scaleLower.includes('minor') || scaleLower.includes('aeolian') || scaleLower.includes('dorian') || scaleLower.includes('phrygian')) {
            setChordOrScale2('minor');
          } else {
            setChordOrScale2('major');
          }
        }
      }
    }

    // Set appropriate right panel view
    if (newMode === 'earTraining') {
      setRightPanelView('earTrainingLearn');
    } else if (newMode === 'chord') {
      if (isStandardTuning(tuning, strings)) {
        setRightPanelView('chordFingeringSelector');
      } else {
        setRightPanelView('keyboard');
      }
      // Notes will be refreshed automatically by useEffect when isChord changes
    } else {
      // Scale mode - only show diatonic chords if skill level allows
      if (skillFeatures.showDiatonicChords) {
        setRightPanelView('diatonicChords');
      } else {
        setRightPanelView('scaleInfo');
      }
      // Notes will be refreshed automatically by useEffect when isChord changes
    }
  };

  // Handle tab changes and restore notes when needed
  const handleTabChange = (newTab: string) => {
    const oldTab = rightPanelView;
    setRightPanelView(newTab as any);

    // If switching away from Practice mode in Ear Training, restore notes
    if (appMode === 'earTraining' && oldTab === 'earTrainingPractice' && newTab !== 'earTrainingPractice') {
      refetchNotes();
    }
  };
  
  // Legacy toggle function for backward compatibility
  const toggleChordOrScale = () => {
    const newMode = appMode === 'chord' ? 'scale' : 'chord';
    handleModeChange(newMode);
  };

  const handleConfigChange = (newStrings: number, newFrets: number, newTuning: string[]) => {
    setStrings(newStrings);
    setFrets(newFrets);
    setTuning(newTuning);
    
    // Adjust fret range if it exceeds the new fret count
    if (fretRange.end > newFrets) {
      setFretRange({ start: 0, end: newFrets });
    }
    
    // Smart panel selection based on new tuning
    if (isChord) {
      if (isStandardTuning(newTuning, newStrings)) {
        setRightPanelView('chordFingeringSelector'); // Fixed: use correct tab value
      } else {
        setRightPanelView('keyboard');
      }
    }
  };

  // Helper to parse scale information from example payload JSON
  const parseScaleFromPayload = (payload?: string | null): { key: string; scale: string } | null => {
    if (!payload) return null;
    try {
      const parsed = JSON.parse(payload);
      // Support both old format (scale) and new format (entityType)
      if (parsed.key && (parsed.scale || parsed.entityType)) {
        return { key: parsed.key, scale: parsed.scale || parsed.entityType };
      }
    } catch (e) {
      console.warn('Failed to parse example payload:', e);
    }
    return null;
  };

  // Helper to parse chord information from example payload JSON
  const parseChordFromPayload = (payload?: string | null): { key: string; chord: string } | null => {
    if (!payload) return null;
    try {
      const parsed = JSON.parse(payload);
      // Support new ML-extracted format (key, entityType, displayName)
      if (parsed.key && parsed.entityType) {
        return { key: parsed.key, chord: parsed.entityType };
      }
    } catch (e) {
      console.warn('Failed to parse chord payload:', e);
    }
    return null;
  };

  // Helper to parse scale information from chat messages
  const parseScaleFromMessages = (messages: any[]): { key: string; scale: string } | null => {
    // Look at the last few messages (both user and assistant) for scale patterns
    const recentMessages = messages
      .slice(-5) // Last 5 messages (user + assistant)
      .map(m => m.content);

    // First try to match patterns with a key like "A Mixolydian", "C# major", etc.
    const scalePatternWithKey = /\b([A-G][#b]?)\s+((?:ionian|dorian|phrygian|lydian|mixolydian|aeolian|locrian|harmonic\s+minor|melodic\s+minor|natural\s+minor|minor\s+pentatonic|major\s+pentatonic|pentatonic\s+major|pentatonic\s+minor|pentatonic|major|minor|blues|whole\s+tone|chromatic)(?:\s+(?:scale|mode))?)\b/i;

    for (const message of recentMessages.reverse()) {
      const match = message.match(scalePatternWithKey);
      if (match) {
        return {
          key: match[1],
          scale: match[2].trim()
        };
      }
    }

    // If no key found, try to match just the scale/mode name and default to C
    const scalePatternNoKey = /\b((?:ionian|dorian|phrygian|lydian|mixolydian|aeolian|locrian|harmonic\s+minor|melodic\s+minor|natural\s+minor|minor\s+pentatonic|major\s+pentatonic|pentatonic\s+major|pentatonic\s+minor|pentatonic|major|minor|blues|whole\s+tone|chromatic)(?:\s+(?:scale|mode))?)\b/i;

    for (const message of recentMessages.reverse()) {
      const match = message.match(scalePatternNoKey);
      if (match) {
        return {
          key: 'C', // Default to C if no key specified
          scale: match[1].trim()
        };
      }
    }

    return null;
  };

  const handleChatCapabilityLink = (link: ChatCapabilityLinkDto) => {
    console.log('Capability link clicked:', link);

    switch (link.capabilityCode) {
      case 'FRETBOARD_QUIZ':
        if (appMode !== 'earTraining') {
          handleModeChange('earTraining');
        }
        setPracticeSubMode('fretboardQuiz');
        setRightPanelView('earTrainingPractice');
        break;

      case 'FRET_SHOW_CHORD':
        // Switch to chord mode if not already
        if (appMode !== 'chord') {
          handleModeChange('chord');
        }

        // Parse chord information from description (e.g., "D m", "Cmaj7", "C Major 7")
        const parseChordFromText = (text: string): { key: string; chord: string } | null => {
          // Match patterns like "D m", "Cmaj7", "F# minor", "C Major 7", etc.
          // IMPORTANT: Match full words (major/minor) BEFORE abbreviations (maj/min) to prevent early cutoff
          const chordMatch = text.match(/\b([A-G][#b]?)\s*((?:major|minor|maj|min|m|M|aug|dim|sus)\s*(?:\d+)?(?:[#b](?:5|9|11|13))?)?/i);
          if (chordMatch) {
            const key = chordMatch[1];
            let chordType = (chordMatch[2] || '').trim();

            // Normalize chord type to match musicOptions values and API expectations
            if (!chordType) {
              chordType = 'major';  // Default to major if no chord type specified
            } else if (chordType.toLowerCase() === 'major') {
              chordType = 'major';  // Keep as 'major' for API
            } else if (chordType.toLowerCase() === 'minor') {
              chordType = 'minor';  // Minor triad
            } else if (chordType.toLowerCase().startsWith('major')) {
              // "Major 7" → "major7", "Major 9" → "major9"
              const number = chordType.substring(5).trim();
              chordType = `major${number}`;
            } else if (chordType.toLowerCase().startsWith('minor')) {
              // "Minor 7" → "minor7", "Minor 9" → "minor9"
              const number = chordType.substring(5).trim();
              chordType = `minor${number}`;
            }
            // Keep maj7, m7, dim, aug, sus formats as-is

            return { key, chord: chordType };
          }
          return null;
        };

        // Prioritize ML-extracted structured data from examplePayload
        const chordInfo = parseChordFromPayload(link.examplePayload)
          || (link.description ? parseChordFromText(link.description) : null);

        console.log('Chord info sources:', {
          fromPayload: parseChordFromPayload(link.examplePayload),
          fromDescription: link.description ? parseChordFromText(link.description) : null,
          final: chordInfo
        });

        if (chordInfo) {
          console.log('Setting chord from chat:', chordInfo);
          setKey(chordInfo.key);

          // Find matching chord in chordOptions
          const matchingChord = chordOptions.find(opt => {
            const normalizedValue = opt.value.toLowerCase().replace(/\s+/g, '');
            const normalizedInput = chordInfo.chord.toLowerCase().replace(/\s+/g, '');
            return normalizedValue === normalizedInput ||
                   (normalizedInput === 'm' && opt.value.toLowerCase().includes('minor'));
          });

          if (matchingChord) {
            console.log('Matched chord option:', matchingChord);
            setChordOrScale(matchingChord.value);
          } else {
            console.warn('Could not find matching chord for:', chordInfo);
            // Default to the chord type as-is
            setChordOrScale(chordInfo.chord);
          }
        }

        // Ensure right panel shows appropriate view
        if (isStandardTuning(tuning, strings)) {
          setRightPanelView('chordFingeringSelector');
        } else {
          setRightPanelView('keyboard');
        }
        break;

      case 'FRET_SHOW_SCALE':
        // Switch to scale mode if not already
        if (appMode !== 'scale') {
          handleModeChange('scale');
        }
        // Ensure right panel shows scale info
        setRightPanelView('scaleInfo');

        // Try to parse scale information from:
        // 1. The capability link description (most reliable - set by backend)
        // 2. Recent messages
        // 3. Example payload (fallback)
        const parseScaleFromText = (text: string): { key: string; scale: string } | null => {
          // Try with key first
          const withKeyMatch = text.match(/\b([A-G][#b]?)\s+(.*?)(?:\s+(?:scale|mode))?$/i);
          if (withKeyMatch) {
            return { key: withKeyMatch[1], scale: withKeyMatch[2].trim() };
          }
          // Try without key (default to C)
          const scaleMatch = text.match(/\b(ionian|dorian|phrygian|lydian|mixolydian|aeolian|locrian|harmonic minor|melodic minor|natural minor|minor pentatonic|major pentatonic|blues minor|blues major|blues|pentatonic|major|minor)/i);
          if (scaleMatch) {
            return { key: 'C', scale: scaleMatch[1].trim() };
          }
          return null;
        };

        const scaleInfo = (link.description ? parseScaleFromText(link.description) : null)
          || parseScaleFromMessages(messages)
          || parseScaleFromPayload(link.examplePayload);

        console.log('Scale info sources:', {
          fromDescription: link.description,
          parsedFromDescription: link.description ? parseScaleFromText(link.description) : null,
          fromMessages: parseScaleFromMessages(messages),
          fromPayload: parseScaleFromPayload(link.examplePayload),
          final: scaleInfo
        });

        if (scaleInfo) {
          console.log('Setting scale from chat:', scaleInfo);
          setKey(scaleInfo.key);

          // Normalize the scale name for matching
          const normalizeScaleName = (name: string) => {
            let normalized = name.toLowerCase()
              .replace(/\s+/g, '')  // Remove spaces
              .replace(/[()]/g, ''); // Remove parentheses

            // Handle word order variations for pentatonic scales
            if (normalized.includes('minor') && normalized.includes('pentatonic')) {
              normalized = 'pentatonicminor';
            } else if (normalized.includes('major') && normalized.includes('pentatonic')) {
              normalized = 'pentatonicmajor';
            } else if (normalized === 'pentatonic' || normalized === 'pentatonicscale') {
              normalized = 'pentatonicminor'; // Default unqualified pentatonic to minor (most common in rock/blues)
            }
            // Handle word order variations for blues scales
            else if (normalized.includes('minor') && normalized.includes('blues')) {
              normalized = 'bluesminor';
            } else if (normalized.includes('major') && normalized.includes('blues')) {
              normalized = 'bluesmajor';
            } else if (normalized === 'blues') {
              normalized = 'bluesminor'; // Default to minor blues
            }
            // Handle Aeolian/Minor synonyms
            else if (normalized === 'minor' || normalized === 'naturalminor') {
              normalized = 'aeolian';
            }
            // Handle Ionian/Major synonyms
            else if (normalized === 'major') {
              normalized = 'ionian';
            }
            // Handle harmonic minor
            else if (normalized === 'harmonicminor') {
              normalized = 'harmonicminor';
            }
            // Handle melodic minor
            else if (normalized === 'melodicminor') {
              normalized = 'melodicminor';
            }

            return normalized;
          };

          const normalizedInput = normalizeScaleName(scaleInfo.scale);

          // Find the scale option that matches
          const matchingScale = scaleOptions.find(opt => {
            const normalizedDisplay = normalizeScaleName(opt.display);
            const normalizedValue = normalizeScaleName(opt.value);
            return normalizedDisplay === normalizedInput || normalizedValue === normalizedInput;
          });

          if (matchingScale) {
            console.log('Matched scale option:', matchingScale);
            setChordOrScale(matchingScale.value);
          } else {
            console.warn('Could not find matching scale for:', scaleInfo.scale);
          }
        }
        break;

      case 'EAR_TRAINING':
        if (appMode !== 'earTraining') {
          handleModeChange('earTraining');
        }
        setRightPanelView('earTrainingPractice');
        break;

      case 'FRET_ANIMATE_PATH':
        // Extract tab text from recent messages
        const extractTabFromMessage = (content: string): string | null => {
          // Try to find tab notation lines - look for multiple consecutive lines starting with string indicators
          const tabLinePattern = /^[eEbBgGdDaA]\s*\|.+$/gm;
          const matches = content.match(tabLinePattern);

          if (matches && matches.length >= 2) {
            // Found at least 2 tab lines - return them joined
            return matches.join('\n');
          }

          // Also try extracting from markdown code blocks
          const codeBlockPattern = /```(?:tab|tablature|guitar)?\n?((?:[eEbBgGdDaA]\s*\|.+\n?)+)```/i;
          const codeBlockMatch = content.match(codeBlockPattern);
          if (codeBlockMatch) {
            return codeBlockMatch[1].trim();
          }

          return null;
        };

        let tabText: string | null = null;
        for (const message of messages.slice(-5).reverse()) {
          tabText = extractTabFromMessage(message.content);
          if (tabText) {
            console.log('Tab detected in message:', message.content.substring(0, 100));
            break;
          }
        }

        if (tabText) {
          console.log('Parsing tab:', tabText);
          // Parse the tab notation
          const parseResult = parseTab(tabText, {
            ...DEFAULT_TAB_PARSER_CONFIG,
            dashesPerBeat: 4 // 4 dashes = 1 beat (16th note resolution)
          });

          if (parseResult.sequence) {
            console.log('Tab parsed successfully:', parseResult.sequence);
            loadTabSequence(parseResult.sequence);
            // Auto-start playback
            setTimeout(() => tabAnimationDispatch({ type: 'PLAY' }), 100);
          } else {
            console.error('Tab parsing errors:', parseResult.errors);
            alert('Could not parse tab notation. The format may be invalid.');
          }
        } else {
          console.warn('FRET_ANIMATE_PATH triggered but no tab found in last 5 messages');
          console.log('Recent messages:', messages.slice(-5).map(m => m.content.substring(0, 100)));
        }
        break;

      case 'BACKING_START':
        // Switch to jam tracks
        setRightPanelView('jamTracks');
        break;

      default:
        console.warn('Unhandled capability code:', link.capabilityCode);
        break;
    }
  };

  const isStandardTuning = (currentTuning: string[], stringCount: number): boolean => {
    const standardTunings: { [key: number]: string[] } = {
      6: ['E', 'A', 'D', 'G', 'B', 'E'],
      7: ['B', 'E', 'A', 'D', 'G', 'B', 'E'],
      8: ['F#', 'B', 'E', 'A', 'D', 'G', 'B', 'E']
    };

    const standard = standardTunings[stringCount];
    if (!standard) return false;

    return currentTuning.length === standard.length &&
      currentTuning.every((note, index) => note === standard[index]);
  };

  // Handle fretboard clicks in ear training mode
  const handleFretboardClick = (position: FretPosition) => {
    // Only process clicks in ear training mode
    if (appMode !== 'earTraining') return;
    
    // Add the clicked position to user answers
    setUserFretboardAnswers(prev => {
      // Prevent duplicate answers at the same position
      const filtered = prev.filter(
        p => !(p.string === position.string && p.fret === position.fret)
      );
      const newArray = [...filtered, position];
      return newArray;
    });
  };

  // Handle subscription success/cancel pages (must be before any early returns)
  const handleSubscriptionContinue = useCallback(() => {
    setSubscriptionPage(null);
    window.history.pushState({}, '', '/');
  }, []);

  // Check for admin mode after all hooks
  const adminUrlParams = new URLSearchParams(window.location.search);
  const isAdminMode = adminUrlParams.get('admin') === 'true';
  const adminKey = adminUrlParams.get('key');

  // Validate admin access
  if (isAdminMode) {
    const validKey = 'timeline-admin-2024';
    if (adminKey === validKey || sessionStorage.getItem('admin-access') === validKey) {
      // Store admin access
      sessionStorage.setItem('admin-access', validKey);
      // Render admin interface
      return <TimelineAdmin />;
    } else {
      // Invalid admin access, redirect to main app
      window.location.href = '/';
      return null;
    }
  }

  // Render subscription pages if on those routes
  if (subscriptionPage === 'success') {
    return <SubscriptionSuccess onContinue={handleSubscriptionContinue} />;
  }
  if (subscriptionPage === 'cancel') {
    return (
      <SubscriptionCancel
        onContinue={handleSubscriptionContinue}
        onTryAgain={() => {
          handleSubscriptionContinue();
          setShowPricingModal(true);
        }}
      />
    );
  }

  // Render jam track mode if active
  if (isJamTrackMode && selectedBackingTrack) {
    return <JamTrack
      track={selectedBackingTrack}
      currentKey={key}
      currentScale={chordOrScale}
      selectedTuning={tuning}
      fretRange={fretRange}
      totalFrets={frets}
      isVertical={isVertical}
      onFretRangeChange={setFretRange}
      onExit={handleExitJamTrack}
    />;
  }

  return (
    <>
      {/* Demo Mode Banner */}
      {demoUser && showDemoBanner && (
        <DemoBanner
          demoUser={demoUser}
          onDismiss={() => setShowDemoBanner(false)}
        />
      )}

      {/* Anonymous User Banner */}
      {isAnonymous && showAnonymousBanner && !demoUser && (
        <AnonymousBanner
          onDismiss={() => setShowAnonymousBanner(false)}
        />
      )}

      <div className="min-h-screen bg-background">
      {/* Modern Header */}
      <header className="border-b bg-card">
        <div className="flex items-center justify-between px-2 py-3 md:p-4">
          {/* App Title & Skill Level */}
          <div className="flex items-center gap-2 md:gap-4">
            <div className="flex items-center gap-2">
              <Guitar className="h-6 w-6" />
              <h1 className="text-xl font-semibold hidden sm:block">Guituitive</h1>
            </div>
            {/* Skill Level Selector */}
            <Select value={skillLevel} onValueChange={(value: SkillLevel) => setSkillLevel(value)}>
              <SelectTrigger className="w-[130px] h-8 text-xs">
                <GraduationCap className="h-3 w-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Current Selection Badge - Desktop only (mobile has MobileControlBar) */}
          <Badge variant="secondary" className="current-selection-badge text-xs md:text-sm hidden md:inline-flex">
            {appMode === 'compare' ? (
              <>
                {key1} {getDisplayName(chordOrScale1, isChord1)} | {key2} {getDisplayName(chordOrScale2, isChord2)}
              </>
            ) : (
              <>
                {key} {getDisplayName(chordOrScale, isChord)} {isChord ? 'chord' : 'scale'}
              </>
            )}
          </Badge>

          {/* Auth & Info Buttons */}
          <div className="flex items-center gap-1 md:gap-2">
            <SubscriptionBadge />
            <UpgradeButton size="sm" className="text-xs px-2">
              <span className="hidden lg:inline">Upgrade to Pro</span>
              <span className="hidden md:inline lg:hidden">Upgrade</span>
              <span className="md:hidden">Pro</span>
            </UpgradeButton>
            {/* News */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsNewsDialogOpen(true)}
              title="View daily guitar news"
              className="hidden sm:inline-flex"
            >
              <Newspaper className="h-4 w-4" />
            </Button>
            {/* Contributors */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCreatorDialogOpen(true)}
              title="View contributor profiles"
            >
              <Users className="h-4 w-4" />
            </Button>
            {/* Tutorial / Help - Desktop only */}
            {!isMobile && (
              <Button
                variant="ghost"
                size="sm"
                onClick={showOnboarding}
                title="Start tutorial"
                className={`relative ${shouldShowTutorialPrompt ? 'tutorial-button-pulse' : ''}`}
              >
                <Lightbulb className="h-4 w-4" />
                <TutorialPrompt
                  isVisible={shouldShowTutorialPrompt}
                  onDismiss={dismissTutorialPrompt}
                  onStart={showOnboarding}
                />
              </Button>
            )}
            {/* About */}
            <Link to="/about">
              <Button variant="outline" size="sm">
                <Info className="h-4 w-4" />
              </Button>
            </Link>
          <AuthButtons />
          </div>
        </div>
      </header>

      {/* Mobile Control Bar - Top controls for mobile */}
      {isMobile && (
        <MobileControlBar
          currentKey={key}
          chordOrScale={chordOrScale}
          isChord={isChord}
          appMode={appMode}
          accidentalPreference={accidentalPreference}
          onKeyChange={setKey}
          onChordOrScaleChange={setChordOrScale}
          onModeChange={handleModeChange}
          onAccidentalPreferenceChange={setAccidentalPreference}
          onSettingsClick={() => setMobileToolsSheetOpen(true)}
          getDisplayName={getDisplayName}
          isAnonymous={isAnonymous}
          isFree={isFree}
        />
      )}

      {/* Mobile Tools Sheet */}
      <MobileToolsSheet
        isOpen={mobileToolsSheetOpen}
        onOpenChange={setMobileToolsSheetOpen}
        strings={strings}
        frets={frets}
        tuning={tuning}
        onConfigChange={handleConfigChange}
        fretRange={fretRange}
        onFretRangeChange={setFretRange}
        showNoteNames={showNoteNames}
        onShowNoteNamesChange={setShowNoteNames}
        isFree={isFree}
      />

      {/* News Dialog */}
      <Dialog open={isNewsDialogOpen} onOpenChange={setIsNewsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogTitle>Daily Guitar News</DialogTitle>
          <DailyNewsPanel showHeading={false} />
        </DialogContent>
      </Dialog>
      <CreatorDirectoryDialog
        open={isCreatorDialogOpen}
        onOpenChange={setCreatorDialogOpen}
        creators={creators}
      />


      {/* Main layout - adjust height for mobile control bar */}
      <div className={`flex ${isMobile ? 'h-[calc(100vh-113px)]' : 'h-[calc(100vh-73px)]'}`}>
        {/* Left Sidebar - Desktop Only */}
        {!isMobile && (
        <div className={`border-r bg-card transition-all duration-300 ${
          sidebarCollapsed ? 'w-12' : 'w-80'
        }`}>
          {/* Sidebar Toggle */}
          <div className="p-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="left-menu-button w-full justify-start"
            >
              <Menu className="h-4 w-4" />
              {!sidebarCollapsed && <span className="ml-2">Controls</span>}
            </Button>
          </div>

          {!sidebarCollapsed && (
            <div className="p-4 space-y-6">
              {/* Mode Toggle */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Mode</label>
                {isMobile ? (
                  // Mobile: single row with 3 buttons
                  <div className="mode-selector grid grid-cols-3 gap-1">
                    <Button
                      variant={appMode === 'scale' ? "default" : "outline"}
                      onClick={() => handleModeChange('scale')}
                      className="text-xs h-8"
                    >
                      Scales
                    </Button>
                    <Button
                      variant={appMode === 'chord' ? "default" : "outline"}
                      onClick={() => handleModeChange('chord')}
                      className="text-xs h-8"
                    >
                      Chords
                    </Button>
                    <Button
                      variant={appMode === 'earTraining' ? "default" : "outline"}
                      onClick={() => handleModeChange('earTraining')}
                      className="text-xs h-8"
                    >
                      <Headphones className="h-3 w-3 mr-1" />
                      Practice
                    </Button>
                  </div>
                ) : (
                  // Desktop: two rows (2x2 grid)
                  <div className="space-y-1">
                    <div className="mode-selector grid grid-cols-2 gap-1">
                      <Button
                        variant={appMode === 'scale' ? "default" : "outline"}
                        onClick={() => handleModeChange('scale')}
                        className="text-xs h-8"
                      >
                        Scales
                      </Button>
                      <Button
                        variant={appMode === 'chord' ? "default" : "outline"}
                        onClick={() => handleModeChange('chord')}
                        className="text-xs h-8"
                      >
                        Chords
                      </Button>
                    </div>
                    <div className={`mode-selector grid gap-1 ${skillFeatures.showDualViewMode ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      <Button
                        variant={appMode === 'earTraining' ? "default" : "outline"}
                        onClick={() => handleModeChange('earTraining')}
                        className="text-xs h-8"
                      >
                        <Headphones className="h-3 w-3 mr-1" />
                        Practice
                      </Button>
                      {skillFeatures.showDualViewMode && (
                        <Button
                          variant={appMode === 'compare' ? "default" : "outline"}
                          onClick={() => handleModeChange('compare')}
                          className="text-xs h-8"
                        >
                          Dual View
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Compare Mode: Fretboard A Controls */}
              {appMode === 'compare' ? (
                <>
                  <div className="p-2 bg-primary/10 rounded-md">
                    <h3 className="text-xs font-semibold text-center">Fretboard A</h3>
                  </div>

                  {/* Fretboard A Mode Toggle */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Type</label>
                    <div className="grid grid-cols-2 gap-1">
                      <Button
                        variant={!isChord1 ? "default" : "outline"}
                        onClick={() => {
                          setIsChord1(false);
                          setChordOrScale1(scaleOptions[0]?.value || '');
                        }}
                        className="text-xs h-8"
                      >
                        Scale
                      </Button>
                      <Button
                        variant={isChord1 ? "default" : "outline"}
                        onClick={() => {
                          setIsChord1(true);
                          setChordOrScale1(chordOptions[0]?.value || '');
                        }}
                        className="text-xs h-8"
                      >
                        Chord
                      </Button>
                    </div>
                  </div>

                  {/* Fretboard A Key and accidentals */}
                  <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] items-end">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Key</label>
                      <Select value={key1} onValueChange={setKey1}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {renderKeyOptions()}
                        </SelectContent>
                      </Select>
                    </div>
                    {renderAccidentalPreferenceControl()}
                  </div>

                  {/* Fretboard A Chord/Scale Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      {isChord1 ? 'Chord Type' : 'Scale Type'}
                    </label>
                    <Select value={chordOrScale1} onValueChange={setChordOrScale1}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(isChord1 ? chordOptions : scaleOptions).map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.display}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  {/* Normal Mode Controls */}
                  {/* Key and accidentals */}
                  <div className="space-y-2">
                    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] items-end">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Key</label>
                        <Select
                          value={key}
                          onValueChange={setKey}
                        >
                          <SelectTrigger className="mobile-select w-full md:w-auto">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {renderKeyOptions()}
                          </SelectContent>
                        </Select>
                      </div>
                      {renderAccidentalPreferenceControl()}
                    </div>
                    {isAnonymous && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Limited to C major.{' '}
                        <SignUpButton mode="modal">
                          <button className="text-primary hover:underline">Sign up free</button>
                        </SignUpButton> for pentatonics + AI chat
                      </p>
                    )}
                    {isFree && !isAnonymous && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Free tier limited to key of C.{' '}
                        <a href="/pricing" className="text-primary hover:underline">Upgrade</a>
                      </p>
                    )}
                  </div>

                  {/* Chord/Scale Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      {isChord ? 'Chord Type' : 'Scale Type'}
                    </label>
                    <Select
                      value={chordOrScale}
                      onValueChange={setChordOrScale}
                    >
                      <SelectTrigger className="mobile-select w-full md:w-auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(isChord ? chordOptions : scaleOptions).map(option => {
                          const isAllowed = isChord
                            ? isChordAvailableForFree(option.value)
                            : isScaleAvailableForFree(option.value);
                          const isLocked = (isAnonymous || isFree) && !isAllowed;

                          return (
                            <SelectItem
                              key={option.value}
                              value={option.value}
                              disabled={isLocked}
                              className={isLocked ? 'opacity-60' : ''}
                            >
                              <span className="flex items-center gap-2">
                                {option.display}
                                {isLocked && (
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-indigo-500/10 text-indigo-600 border-indigo-500/30">
                                    Pro
                                  </Badge>
                                )}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {isAnonymous && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Limited to C major.{' '}
                        <SignUpButton mode="modal"><button className="text-primary hover:underline">Sign up free</button></SignUpButton> for pentatonics + AI chat
                      </p>
                    )}
                    {isFree && !isAnonymous && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Free: Major, Minor, Pentatonics.{' '}
                        <a href="/pricing" className="text-primary hover:underline">Upgrade</a> for all modes
                      </p>
                    )}
                  </div>
                </>
              )}
              
              {/* Instrument Configuration - hidden for beginners */}
              {skillFeatures.showInstrumentConfig && (
                <>
                  <Separator />
                  <div className="instrument-config">
                    <InstrumentConfig
                      onConfigChange={handleConfigChange}
                      currentStrings={strings}
                      currentFrets={frets}
                      currentTuning={tuning}
                      isFree={isFree}
                    />
                  </div>
                </>
              )}

              {/* Fret Range Selector - hidden for beginners */}
              {skillFeatures.showFretRangeSelector && (
                <>
                  <Separator />
                  <FretRangeSelector
                    fretRange={fretRange}
                    totalFrets={frets}
                    onRangeChange={setFretRange}
                    isMobile={typeof window !== 'undefined' && window.innerWidth < 768}
                  />
                </>
              )}

              <Separator />
              {/* Display Settings */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Display Settings</h3>

                {/* Note Names Toggle */}
                <div className="flex items-center justify-between">
                  <label className="text-sm">Show Note Names</label>
                  <Switch
                    checked={showNoteNames}
                    onCheckedChange={setShowNoteNames}
                  />
                </div>

                {/* Orientation Toggle - only show on desktop/tablet (not mobile) */}
                {!isMobile && (
                  <div className="space-y-2">
                    <label className="text-sm">Orientation</label>
                    <div className="flex gap-2">
                      <Button
                        variant={isVertical ? "default" : "outline"}
                        size="sm"
                        onClick={() => setIsVertical(true)}
                      >
                        Vertical
                      </Button>
                      <Button
                        variant={!isVertical ? "default" : "outline"}
                        size="sm"
                        onClick={() => setIsVertical(false)}
                      >
                        Horizontal
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Backing Track Panel - show in sidebar when horizontal orientation */}
              {appMode === 'scale' && !isJamTrackMode && !isChord && !isVertical && (
                <BackingTrackPanel
                  currentKey={key}
                  currentScale={chordOrScale}
                  onTrackSelect={handleTrackSelect}
                  isVisible={true}
                  onTracksCountChange={handleTracksCountChange}
                  isAnonymous={isAnonymous}
                />
              )}

            </div>
          )}
        </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 px-2 py-2 md:p-4 flex overflow-auto">
          {/* Compare Mode: Dual Fretboards */}
          {appMode === 'compare' ? (
            <div className={`flex-1 flex gap-4 p-4 ${isVertical ? 'flex-row' : 'flex-col'}`}>
              {/* Fretboard A */}
              <div className="flex-1 flex flex-col items-center">
                <div className="mb-2 p-2 bg-primary/10 rounded-md w-full">
                  <h3 className="text-sm font-semibold text-center">
                    {key1.toUpperCase()} {getDisplayName(chordOrScale1, isChord1)}
                  </h3>
                </div>
                <Fretboard
                  noteCollection={noteCollection1}
                  isVertical={isVertical}
                  strings={strings}
                  frets={frets}
                  tuning={tuning}
                  showNoteNames={showNoteNames}
                  fretRange={fretRange}
                />
                <IntervalsLegend
                  noteCollection={noteCollection1}
                  currentKey={key1.toUpperCase()}
                  currentScale={getDisplayName(chordOrScale1, isChord1)}
                />
              </div>

              {/* Fretboard B */}
              <div className="flex-1 flex flex-col items-center">
                <div className="mb-2 p-2 bg-primary/10 rounded-md w-full">
                  <h3 className="text-sm font-semibold text-center">
                    {key2.toUpperCase()} {getDisplayName(chordOrScale2, isChord2)}
                  </h3>
                </div>
                <Fretboard
                  noteCollection={noteCollection2}
                  isVertical={isVertical}
                  strings={strings}
                  frets={frets}
                  tuning={tuning}
                  showNoteNames={showNoteNames}
                  fretRange={fretRange}
                />
                <IntervalsLegend
                  noteCollection={noteCollection2}
                  currentKey={key2.toUpperCase()}
                  currentScale={getDisplayName(chordOrScale2, isChord2)}
                />
              </div>
            </div>
          ) : (
            <>
              {/* Jam Tracks Panel - Desktop: show in scale mode when not in jam track mode and vertical orientation */}
              {appMode === 'scale' && !isJamTrackMode && !isChord && isVertical && !isMobile && (
                <BackingTrackPanel
                  currentKey={key}
                  currentScale={chordOrScale}
                  onTrackSelect={handleTrackSelect}
                  isVisible={true}
                  inMainArea={true}
                  onTracksCountChange={handleTracksCountChange}
                  isAnonymous={isAnonymous}
                />
              )}

              {/* Fretboard Container */}
              <div className="fretboard-container relative flex-1 flex flex-col items-center justify-start pt-2 md:pt-0">
                {/* Helpful Hints Button - Desktop Only */}
                {!isMobile && appMode !== 'earTraining' && !shouldShowTheoryPrimer && !shouldShowTheoryCard && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 z-10 text-muted-foreground hover:text-primary"
                    onClick={showTheoryHints}
                    title="Helpful hints"
                  >
                    <Lightbulb className="h-4 w-4" />
                  </Button>
                )}

                {/* Theory Primer - Educational Context - Desktop Only */}
                {!isMobile && shouldShowTheoryPrimer && appMode !== 'earTraining' && (
                  <div className="w-full max-w-4xl px-4 mb-4">
                    <TheoryPrimer onDismiss={dismissTheoryPrimer} />
                  </div>
                )}

                {/* Theory Card - Pattern Significance - Desktop Only */}
                {!isMobile && shouldShowTheoryCard && appMode !== 'earTraining' && (
                  <div className="w-full max-w-4xl px-4 mb-4">
                    <TheoryCard onDismiss={dismissTheoryCard} />
                  </div>
                )}

                <Fretboard
                noteCollection={tabSequence || (appMode === 'earTraining' && practiceSubMode === 'fretboardQuiz') ? null : noteCollection} // Clear notes when tab is playing or in fretboard quiz mode
                isVertical={isVertical}
                strings={strings}
                frets={frets}
                tuning={tuning}
                showNoteNames={
                  // Hide note names for fretboard quiz practice and test modes
                  practiceSubMode === 'fretboardQuiz' &&
                  (rightPanelView === 'earTrainingPractice' || rightPanelView === 'earTrainingTest')
                    ? false
                    : showNoteNames
                }
                fretRange={fretRange}
                selectedFingering={selectedFingering}
                selectedDiatonicChord={selectedDiatonicChord}
                chordHighlightType={chordHighlightType}
                // Interactive mode: ear training OR tab animation
                interactive={(appMode === 'earTraining' && fretboardInteractive) || !!tabSequence}
                onFretClick={handleFretboardClick}
                rootNotePosition={rootNotePosition}
                userAnswers={userFretboardAnswers}
                correctUserAnswers={correctUserFretboardAnswers}
                incorrectUserAnswers={incorrectUserFretboardAnswers}
                // Use tab animation highlights when playing, otherwise use ear training highlights
                highlightedPositions={
                  tabAnimationHighlights.length > 0
                    ? tabAnimationHighlights
                    : highlightedFretPositions
                }
                // Scale pattern animation
                currentAnimatedNote={!isChord && appMode !== 'compare' ? currentAnimatedNote : null}
              />

                {/* Tab Animation Controls - Floating Overlay */}
                {tabSequence && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 w-full max-w-lg px-4">
                    <div className="bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg">
                      <TabAnimationControls
                        state={tabAnimationState}
                        progress={tabProgress}
                        dispatch={tabAnimationDispatch}
                        tempo={tabSequence.tempo}
                        onClose={() => loadTabSequence(null)}
                      />
                    </div>
                  </div>
                )}

                {isChord && (
                  <p className="mt-2 text-xs text-muted-foreground text-center px-4">
                    These notes show every chord tone across the fretboard. Select a fingering on the right to see common shapes.
                  </p>
                )}

                {/* Intervals Legend - Desktop only (mobile uses accordion) */}
                {!isMobile && (
                  <IntervalsLegend
                    noteCollection={noteCollection}
                    currentKey={key.toUpperCase()}
                    currentScale={getDisplayName(chordOrScale, isChord)}
                    isAnimating={!isChord && appMode !== 'compare' ? isScaleAnimating : false}
                    onToggleAnimation={!isChord && appMode !== 'compare' ? toggleScaleAnimation : undefined}
                  />
                )}

                {/* Mobile Content Accordion - Consolidated mobile UI */}
                {isMobile && appMode === 'scale' && !isJamTrackMode && (
                  <>
                    <MobileContentAccordion
                      noteCollection={noteCollection}
                      currentKey={key}
                      currentScale={getDisplayName(chordOrScale, isChord)}
                      currentScaleValue={chordOrScale}
                      isChord={isChord}
                      onTrackSelect={handleTrackSelect}
                      availableTracksCount={availableTracksCount}
                      onTracksCountChange={handleTracksCountChange}
                      isAnonymous={isAnonymous}
                      showFingerings={isStandardTuning(tuning, strings)}
                      chordRoot={key}
                      chordType={chordOrScale}
                      onFingeringSelect={(fingering) => {
                        setSelectedFingering(fingering);
                        setChordHighlightType(fingering ? 'fingering' : null);
                        setSelectedDiatonicChord(null);
                      }}
                      selectedFingering={selectedFingering}
                      isAnimating={isScaleAnimating}
                      onToggleAnimation={toggleScaleAnimation}
                      defaultOpenSections={['legend']}
                    />

                    {/* Tutorial Panel - Collapsed by default */}
                    <MobileTutorialPanel
                      appMode={appMode}
                      currentKey={key}
                      currentScale={getDisplayName(chordOrScale, isChord)}
                    />

                    {/* Learning Panel - Below Tutorial in Scale Mode */}
                    <MobileLearningPanel
                      appMode={appMode}
                      currentKey={key}
                      currentScale={getDisplayName(chordOrScale, isChord)}
                    />
                  </>
                )}

                {/* Mobile Chord Mode (non-scale) - Still use inline chord fingerings */}
                {isMobile && (appMode !== 'scale' || isJamTrackMode) && isChord && isStandardTuning(tuning, strings) && (
                  <>
                    <div className="w-full mt-2">
                      <MobileChordFingerings
                        chordRoot={key}
                        chordType={chordOrScale}
                        onFingeringSelect={(fingering) => {
                          setSelectedFingering(fingering);
                          setChordHighlightType(fingering ? 'fingering' : null);
                          setSelectedDiatonicChord(null);
                        }}
                        selectedFingering={selectedFingering}
                        fretRange={{ start: fretRange.start, end: fretRange.end }}
                      />
                    </div>

                    {/* Tutorial Panel - Collapsed by default */}
                    <MobileTutorialPanel
                      appMode={appMode}
                      currentKey={key}
                      currentScale={getDisplayName(chordOrScale, isChord)}
                    />

                    {/* Learning Panel - Below Tutorial in Chord Mode */}
                    <MobileLearningPanel
                      appMode={appMode}
                      currentKey={key}
                      currentScale={getDisplayName(chordOrScale, isChord)}
                    />
                  </>
                )}

                {/* Chord Mode with non-standard tuning (no fingerings available) */}
                {isMobile && (appMode !== 'scale' || isJamTrackMode) && isChord && !isStandardTuning(tuning, strings) && (
                  <>
                    <MobileTutorialPanel
                      appMode={appMode}
                      currentKey={key}
                      currentScale={getDisplayName(chordOrScale, isChord)}
                    />
                    <MobileLearningPanel
                      appMode={appMode}
                      currentKey={key}
                      currentScale={getDisplayName(chordOrScale, isChord)}
                    />
                  </>
                )}

                {/* Mobile Practice Panel - shown in earTraining mode */}
                {isMobile && appMode === 'earTraining' && (
                  <MobilePracticePanel
                    currentKey={key}
                    currentScale={getDisplayName(chordOrScale, isChord)}
                    noteCollection={noteCollection}
                    showNoteNames={showNoteNames}
                    onSetShowNoteNames={setShowNoteNames}
                    onSetKey={setKey}
                    onSetScale={setChordOrScale}
                    practiceSubMode={practiceSubMode}
                    setPracticeSubMode={setPracticeSubMode}
                    tuning={tuning}
                    strings={strings}
                    frets={frets}
                    fretRange={fretRange}
                    setFretRange={setFretRange}
                    fretboardInteractive={fretboardInteractive}
                    setFretboardInteractive={setFretboardInteractive}
                    setRootNotePosition={setRootNotePosition}
                    setUserFretboardAnswers={setUserFretboardAnswers}
                    setHighlightedFretPositions={setHighlightedFretPositions}
                    correctUserFretboardAnswers={correctUserFretboardAnswers}
                    setCorrectUserFretboardAnswers={setCorrectUserFretboardAnswers}
                    incorrectUserFretboardAnswers={incorrectUserFretboardAnswers}
                    setIncorrectUserFretboardAnswers={setIncorrectUserFretboardAnswers}
                    onClearFretboard={() => {
                      setFretboardInteractive(false);
                      setRootNotePosition(null);
                      setUserFretboardAnswers([]);
                      setHighlightedFretPositions([]);
                      setCorrectUserFretboardAnswers([]);
                      setIncorrectUserFretboardAnswers([]);
                    }}
                    userFretboardAnswers={userFretboardAnswers}
                    isStandardTuning={isStandardTuning(tuning, strings)}
                  />
                )}
              </div>
            </>
          )}
        </div>

        {/* Right Panel - Desktop Only */}
        {!isMobile && (
        <div className={`border-l bg-card transition-all duration-300 ${
          rightPanelCollapsed ? 'w-12' : 'w-96'
        }`}>
          {/* Right Panel Toggle */}
          <div className="p-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
              className="w-full justify-end"
            >
              {!rightPanelCollapsed && <span className="mr-2">Tools</span>}
              <Menu className="h-4 w-4" />
            </Button>
          </div>

          {!rightPanelCollapsed && (
            <div className="p-4">{/* Compare Mode: Fretboard B Controls */}
            {appMode === 'compare' ? (
              <div className="space-y-6">
                <div className="p-2 bg-primary/10 rounded-md">
                  <h3 className="text-xs font-semibold text-center">Fretboard B</h3>
                </div>

                {/* Fretboard B Mode Toggle */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Type</label>
                  <div className="grid grid-cols-2 gap-1">
                    <Button
                      variant={!isChord2 ? "default" : "outline"}
                      onClick={() => {
                        setIsChord2(false);
                        setChordOrScale2(scaleOptions[0]?.value || '');
                      }}
                      className="text-xs h-8"
                    >
                      Scale
                    </Button>
                    <Button
                      variant={isChord2 ? "default" : "outline"}
                      onClick={() => {
                        setIsChord2(true);
                        setChordOrScale2(chordOptions[0]?.value || '');
                      }}
                      className="text-xs h-8"
                    >
                      Chord
                    </Button>
                  </div>
                </div>

                {/* Fretboard B Key and accidentals */}
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] items-end">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Key</label>
                    <Select value={key2} onValueChange={setKey2}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {renderKeyOptions()}
                      </SelectContent>
                    </Select>
                  </div>
                  {renderAccidentalPreferenceControl()}
                </div>

                {/* Fretboard B Chord/Scale Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {isChord2 ? 'Chord Type' : 'Scale Type'}
                  </label>
                  <Select value={chordOrScale2} onValueChange={setChordOrScale2}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(isChord2 ? chordOptions : scaleOptions).map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.display}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <>
                {/* Practice Sub-Mode Selector */}
                {appMode === 'earTraining' && (
                  <div className="mb-4">
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-muted-foreground">Practice Type</label>
                      <Select value={practiceSubMode} onValueChange={(value: 'earTraining' | 'fretboardQuiz') => setPracticeSubMode(value)}>
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="earTraining">
                            <div className="flex items-center gap-2">
                              <Headphones className="h-3 w-3" />
                              Ear Training
                            </div>
                          </SelectItem>
                          <SelectItem value="fretboardQuiz">
                            <div className="flex items-center gap-2">
                              <Brain className="h-3 w-3" />
                              Fretboard Quiz
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <Tabs value={rightPanelView} onValueChange={handleTabChange}>
              {/* Dynamic TabsList based on current mode */}
              <TabsList className={`grid w-full ${
                appMode === 'earTraining' ? 'grid-cols-4' :
                appMode === 'chord' ? (isStandardTuning(tuning, strings) ? 'grid-cols-2' : 'grid-cols-1') : 'grid-cols-2'
              }`}>
                {/* Ear Training Mode Tabs */}
                {appMode === 'earTraining' && (
                  <>
                    <TabsTrigger value="earTrainingLearn" className="text-xs md:text-sm px-1 md:px-2 py-2 min-h-[44px]">
                      <Volume2 className="h-4 w-4 mr-1" />
                      Learn
                    </TabsTrigger>
                    <TabsTrigger value="earTrainingPractice" className="text-xs md:text-sm px-1 md:px-2 py-2 min-h-[44px]">
                      <Target className="h-4 w-4 mr-1" />
                      Practice
                    </TabsTrigger>
                    <TabsTrigger value="earTrainingTest" className="text-xs md:text-sm px-1 md:px-2 py-2 min-h-[44px]">
                      <Brain className="h-4 w-4 mr-1" />
                      Test
                    </TabsTrigger>
                    <TabsTrigger value="earTrainingProgress" className="text-xs md:text-sm px-1 md:px-2 py-2 min-h-[44px]">
                      <TrendingUp className="h-4 w-4 mr-1" />
                      Progress
                    </TabsTrigger>
                  </>
                )}

                {/* Scale Mode Tabs */}
                {appMode === 'scale' && (
                  <>
                    {skillFeatures.showDiatonicChords && (
                      <TabsTrigger value="diatonicChords" className="text-xs md:text-sm px-1 md:px-2 py-2 min-h-[44px]">
                        <Guitar className="h-4 w-4 mr-1" />
                        Chords
                      </TabsTrigger>
                    )}
                    {skillFeatures.showPianoKeyboard && (
                      <TabsTrigger value="keyboard" className="text-xs md:text-sm px-1 md:px-2 py-2 min-h-[44px]">
                        <KeyboardIcon className="h-4 w-4 mr-1" />
                        Piano
                      </TabsTrigger>
                    )}
                  </>
                )}

                {/* Chord Mode Tabs */}
                {appMode === 'chord' && (
                  <>
                    {isStandardTuning(tuning, strings) && (
                      <TabsTrigger value="chordFingeringSelector" className="text-xs md:text-sm px-1 md:px-2 py-2 min-h-[44px]">
                        <Guitar className="h-4 w-4 mr-1" />
                        Fingerings
                      </TabsTrigger>
                    )}
                    {skillFeatures.showPianoKeyboard && (
                      <TabsTrigger value="keyboard" className="text-xs md:text-sm px-1 md:px-2 py-2 min-h-[44px]">
                        <KeyboardIcon className="h-4 w-4 mr-1" />
                        Piano
                      </TabsTrigger>
                    )}
                  </>
                )}

              </TabsList>

              {appMode === 'scale' && !isChord && (
                <div className="mt-4 rounded-lg border border-dashed border-muted-foreground/60 bg-muted/30 p-3 text-xs text-muted-foreground">
                  Need a deeper explanation or practice ideas? Tap the chat bubble in the bottom-right corner to open the Harmonic Companion.
                </div>
              )}

              {/* Diatonic Chords tab - only available in Scale mode and for intermediate+ */}
              {!isChord && skillFeatures.showDiatonicChords && (
                <TabsContent value="diatonicChords" className="mt-4">
                  <DiatonicChords
                    diatonicChords={diatonicChords}
                    currentKey={key}
                    currentScale={getDisplayName(chordOrScale, isChord)}
                    selectedDiatonicChord={selectedDiatonicChord}
                    onDiatonicChordFingeringSelect={(chord, fingering) => {
                      if (!chord && !fingering) {
                        setSelectedFingering(null);
                        setSelectedDiatonicChord(null);
                        setChordHighlightType(null);
                        return;
                      }
                      setSelectedFingering(null);
                      setSelectedDiatonicChord({ chord, fingering });
                      setChordHighlightType('diatonic');
                    }}
                  />
                </TabsContent>
              )}

              {/* Piano tab - always available */}
              <TabsContent value="keyboard" className="mt-4">
                <Keyboard noteCollection={noteCollection} />
              </TabsContent>

              {/* Fingerings tab - only available for standard tunings in Chord mode */}
              {isChord && isStandardTuning(tuning, strings) && (
                <TabsContent value="chordFingeringSelector" className="mt-4">
                  {(isAnonymous || isFree) && (urlParams.key || urlParams.chordOrScale) && (
                    <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
                      <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-indigo-500/10 text-indigo-600 border-indigo-500/30">
                        Pro
                      </Badge>
                      <span>Fingerings for all keys and chords available with Pro</span>
                    </div>
                  )}
                  <ChordFingeringSelector
                    chordRoot={key}
                    chordType={chordOrScale}
                    onFingeringSelect={(fingering) => {
                      setSelectedFingering(fingering);
                      setChordHighlightType(fingering ? 'fingering' : null);
                      setSelectedDiatonicChord(null);
                    }}
                    selectedFingering={selectedFingering}
                    fretRange={{ start: fretRange.start, end: fretRange.end }}
                    disabled={(isAnonymous || isFree) && !!(urlParams.key || urlParams.chordOrScale)}
                  />
                </TabsContent>
              )}

              {/* Show explanation when in chord mode but non-standard tuning */}
              {isChord && !isStandardTuning(tuning, strings) && rightPanelView === 'chordFingeringSelector' && (
                <TabsContent value="chordFingeringSelector" className="mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Chord Fingerings</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-2">
                        Chord fingerings are only available for standard tunings.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Current tuning: {tuning.join('-')} ({strings} strings)
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Switch to standard tuning in Instrument Setup to see chord fingerings.
                      </p>
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {/* Practice Mode Tabs - Context Aware */}
              <TabsContent value="earTrainingLearn" className="mt-4">
                {practiceSubMode === 'earTraining' ? (
                  <EarTrainingLearn 
                    currentKey={key}
                    currentScale={getDisplayName(chordOrScale, isChord)}
                    noteCollection={noteCollection}
                  />
                ) : (
                  <FretboardLearn 
                    currentKey={key}
                    currentScale={getDisplayName(chordOrScale, isChord)}
                    showNoteNames={showNoteNames}
                    onSetKey={setKey}
                    onSetScale={setChordOrScale}
                    onSetShowNoteNames={setShowNoteNames}
                  />
                )}
              </TabsContent>

              <TabsContent value="earTrainingPractice" className="mt-4">
                {practiceSubMode === 'earTraining' ? (
                  <EarTrainingPractice
                    currentKey={key}
                    currentScale={getDisplayName(chordOrScale, isChord)}
                    noteCollection={noteCollection}
                    tuning={tuning}
                    // Fretboard control props
                    fretboardInteractive={fretboardInteractive}
                    setFretboardInteractive={setFretboardInteractive}
                    setRootNotePosition={setRootNotePosition}
                    userFretboardAnswers={userFretboardAnswers}
                    setUserFretboardAnswers={setUserFretboardAnswers}
                    setHighlightedFretPositions={setHighlightedFretPositions}
                    fretRange={fretRange}
                  />
                ) : (
                  <FretboardPractice 
                    currentKey={key}
                    currentScale={getDisplayName(chordOrScale, isChord)}
                    noteCollection={noteCollection}
                    tuning={tuning}
                    fretboardInteractive={fretboardInteractive}
                    setFretboardInteractive={setFretboardInteractive}
                    setRootNotePosition={setRootNotePosition}
                    userFretboardAnswers={userFretboardAnswers}
                    setUserFretboardAnswers={setUserFretboardAnswers}
                    setHighlightedFretPositions={setHighlightedFretPositions}
                    correctUserFretboardAnswers={correctUserFretboardAnswers}
                    setCorrectUserFretboardAnswers={setCorrectUserFretboardAnswers}
                    incorrectUserFretboardAnswers={incorrectUserFretboardAnswers}
                    setIncorrectUserFretboardAnswers={setIncorrectUserFretboardAnswers}
                    fretRange={fretRange}
                    setFretRange={setFretRange}
                    isStandardTuning={isStandardTuning(tuning, strings)}
                    onClearFretboard={() => {
                      setNoteCollection({ notesAndIntervals: [] });
                      setSelectedFingering(null);
                      setSelectedDiatonicChord(null);
                      setChordHighlightType(null);
                      setDiatonicChords([]);
                    }}
                  />
                )}
              </TabsContent>

              <TabsContent value="earTrainingTest" className="mt-4">
                {practiceSubMode === 'earTraining' ? (
                  <EarTrainingTest 
                    currentKey={key}
                    currentScale={getDisplayName(chordOrScale, isChord)}
                    noteCollection={noteCollection}
                  />
                ) : (
                  <FretboardTest 
                    currentKey={key}
                    currentScale={getDisplayName(chordOrScale, isChord)}
                    noteCollection={noteCollection}
                    tuning={tuning}
                    // Fretboard control props
                    fretboardInteractive={fretboardInteractive}
                    setFretboardInteractive={setFretboardInteractive}
                    setRootNotePosition={setRootNotePosition}
                    userFretboardAnswers={userFretboardAnswers}
                    setUserFretboardAnswers={setUserFretboardAnswers}
                    setHighlightedFretPositions={setHighlightedFretPositions}
                    correctUserFretboardAnswers={correctUserFretboardAnswers}
                    setCorrectUserFretboardAnswers={setCorrectUserFretboardAnswers}
                    incorrectUserFretboardAnswers={incorrectUserFretboardAnswers}
                    setIncorrectUserFretboardAnswers={setIncorrectUserFretboardAnswers}
                    fretRange={fretRange}
                    isStandardTuning={isStandardTuning(tuning, strings)}
                    onClearFretboard={() => {
                      // Clear all fretboard visual state
                      setNoteCollection({ notesAndIntervals: [] });
                      setDiatonicChords([]);
                      setUserFretboardAnswers([]);
                      setCorrectUserFretboardAnswers([]);
                      setIncorrectUserFretboardAnswers([]);
                      setHighlightedFretPositions([]);
                      setRootNotePosition(null);
                      setSelectedFingering(null);
                      setSelectedDiatonicChord(null);
                    }}
                  />
                )}
              </TabsContent>

              <TabsContent value="earTrainingProgress" className="mt-4">
                {practiceSubMode === 'earTraining' ? (
                  <EarTrainingProgress 
                    currentKey={key}
                    currentScale={getDisplayName(chordOrScale, isChord)}
                  />
                ) : (
                  <FretboardProgress 
                    currentKey={key}
                    currentScale={getDisplayName(chordOrScale, isChord)}
                  />
                )}
              </TabsContent>
            </Tabs>
              </>
            )}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Onboarding Guide - Desktop only */}
      {!isMobile && (
        <OnboardingGuide
          isVisible={shouldShowOnboarding}
          onComplete={completeOnboarding}
          onSkip={skipOnboarding}
        />
      )}
    </div>
    {/* Chat available to all users - anonymous get 1 free question */}
    <GlobalChat onCapabilityLink={handleChatCapabilityLink} />

    {/* Pricing Modal */}
    <PricingModal open={showPricingModal} onOpenChange={setShowPricingModal} />

    <div className="pointer-events-none fixed bottom-3 right-3 z-40 rounded border border-border/60 bg-card/80 px-2 py-1 text-[11px] text-muted-foreground/80 shadow-sm backdrop-blur hidden md:block">
      © 2024-2026 Entangled Harmony Labs, LLC
    </div>
  </>
);
};

export default App;






